/**
 * TemplateAssignmentsPanel
 *
 * Who a `registered_user` form template is currently assigned to, and the way
 * to take an assignment back.
 *
 * Assigning was previously write-only: the dialog created a form_instance with
 * `assigned_to_user_id` and said nothing more. Somos assigned three forms to a
 * promotora and had no way to tell from the UI whether it had worked — no list,
 * no count, no link, and no way to undo a mistake. (It had worked: instances
 * 7350-7352 were sitting in the table.) This is the read side of that action.
 *
 * "Remove" deletes the assignee's DRAFT instance, which is what un-assigning
 * means for a form nobody has filled in yet. A submitted form is a record and
 * is never deleted here — those rows render without a Remove button.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Trash2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formsAPI, FormInstance } from '@/services/api/forms';

interface TemplateAssignmentsPanelProps {
  templateId: number;
  /** Bumped by the parent after a new assignment so the list refetches. */
  refreshKey?: number;
}

const fmtDate = (raw?: string | null): string | null => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export const TemplateAssignmentsPanel: React.FC<TemplateAssignmentsPanelProps> = ({
  templateId,
  refreshKey = 0,
}) => {
  const [rows, setRows] = useState<FormInstance[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const load = useCallback(() => {
    setRows(null);
    setError(null);
    formsAPI
      .listFormInstances({ form_template_id: templateId, page_size: 100 })
      .then((r) => setRows(r || []))
      .catch((e: any) => setError(e?.message || 'Failed to load assignments'));
  }, [templateId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const remove = async (instance: FormInstance) => {
    const who = instance.assigned_to_name || `user #${instance.assigned_to_user_id}`;
    if (!confirm(`Remove this assignment from ${who}? The blank draft will be deleted.`)) return;
    setRemovingId(instance.id);
    setError(null);
    try {
      await formsAPI.deleteFormInstance(instance.id);
      setRows((prev) => (prev || []).filter((r) => r.id !== instance.id));
    } catch (e: any) {
      setError(e?.message || 'Failed to remove the assignment');
    } finally {
      setRemovingId(null);
    }
  };

  if (rows === null && !error) {
    return (
      <div className="text-sm text-gray-500 py-4 inline-flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading assignments…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-sm text-red-700 bg-red-50 dark:bg-red-900/20 rounded p-2 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {(rows || []).length === 0 ? (
        <p className="text-sm text-gray-500 py-2">
          Not assigned to anyone yet. Use <strong>Assign</strong> to send this form to a staff
          member — it appears in their <strong>My Forms</strong> inbox.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <th className="py-2 pr-3">Assigned to</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Assigned</th>
              <th className="py-2 pr-3">Due</th>
              <th className="py-2 pr-3">Progress</th>
              <th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {(rows || []).map((inst) => {
              const isDraft = inst.status === 'draft';
              return (
                <tr key={inst.id}>
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                    {inst.assigned_to_name || `User #${inst.assigned_to_user_id ?? '—'}`}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge variant={isDraft ? 'secondary' : 'success'} size="sm" className="capitalize">
                      {inst.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">
                    {/* assigned_date went unset for a long time; fall back to
                        when the instance was created, which is the same moment. */}
                    {fmtDate((inst as any).assigned_date) || fmtDate((inst as any).created_ts) || '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">
                    {fmtDate(inst.due_date) || '—'}
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-400">
                    {inst.completion_percentage != null
                      ? `${Math.round(Number(inst.completion_percentage))}%`
                      : '—'}
                  </td>
                  <td className="py-2 text-right">
                    {isDraft ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(inst)}
                        disabled={removingId === inst.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Remove this assignment"
                        aria-label="Remove this assignment"
                      >
                        {removingId === inst.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    ) : (
                      // Submitted work is a record — un-assigning it would delete
                      // someone's answers.
                      <span className="text-xs text-gray-400">Submitted — kept</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TemplateAssignmentsPanel;
